/**
 * Hook `useClueOverlay` — active la détection des CLICS DE MOTS dans un
 * conteneur DOM (le "scope") pendant la chasse aux indices.
 *
 * Approche simple et robuste : DÉLÉGATION D'ÉVÉNEMENT sur le conteneur. Au
 * clic, on résout le texte cliqué via `caretRangeFromPoint` /
 * `caretPositionFromPoint` (ratio par navigateur), on étend au mot le plus
 * proche via l'API TextRange sur les caractères + un pattern regex, puis on
 * transmet le token trouvé à `onWordClick`. Le parent (ClueHuntPanel) décide
 * s'il matche un indice via `findClueByClick`.
 *
 * On ne "colore" pas les mots individuellement pour ne pas se battre contre la
 * mise en forme du markdown / KaTeX — le retour visuel se fait via la popup et
 * les cases indices qui se remplissent.
 *
 * ⚠️ Ne matche PAS si l'élément cliqué est dans une équation KaTeX rendue
 * (`.katex` : le DOM est du SVG/spans qui ne portent pas de textNode ordinaire).
 * On lit alors le texte source via l'attribut `title` du wrapper `.katex-html`
 * s'il existe, sinon on tombe sur l'outerText — meilleur effort.
 */

import { useEffect } from 'react';

/**
 * Résout la sous-chaîne cliquée dans un event. Renvoie le "mot" pointé (extrait
 * à l'aide de la sélection browser autour du caret) — soit un token en clair,
 * soit une expression LaTeX rendue par KaTeX (texte source réhydraté au
 * mieux). null si on n'a rien pu extraire.
 */
export function extractClickedText(evt: MouseEvent | TouchEvent): string | null {
  const point = pointOf(evt);
  if (!point) return null;

  // 1) Si on a cliqué dans une équation KaTeX rendue, on essaie de lire le
  //    texte source via l'attribut aria-label ou l'annotation MathML.
  const targetEl = evt.target as Element | null;
  const katexRoot = targetEl?.closest('.katex') as HTMLElement | null;
  if (katexRoot) {
    const annotation = katexRoot.querySelector('annotation[encoding="application/x-tex"]');
    const src = annotation?.textContent?.trim();
    if (src) return src;
    // Fallback : le nœud aria-label sur .katex-mathml.
    const aria = katexRoot.querySelector('[aria-label]')?.getAttribute('aria-label');
    if (aria) return aria.trim();
    return katexRoot.textContent?.trim() || null;
  }

  // 2) Cas normal : on utilise l'API caret{Range,Position}FromPoint pour trouver
  //    le node texte + offset, puis on étend au mot autour (regex).
  const caret = resolveCaret(point.clientX, point.clientY);
  if (!caret) return null;
  const { node, offset } = caret;
  const raw = node.textContent ?? '';
  if (!raw) return null;
  return wordAround(raw, offset);
}

interface CaretHit {
  node: Text;
  offset: number;
}

function resolveCaret(x: number, y: number): CaretHit | null {
  // Standard (Firefox / Chromium récents) : caretPositionFromPoint.
  const dAny = document as unknown as {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  const pos = dAny.caretPositionFromPoint?.(x, y);
  if (pos && pos.offsetNode.nodeType === Node.TEXT_NODE) {
    return { node: pos.offsetNode as Text, offset: pos.offset };
  }
  // WebKit legacy : caretRangeFromPoint.
  const r = dAny.caretRangeFromPoint?.(x, y);
  if (r && r.startContainer.nodeType === Node.TEXT_NODE) {
    return { node: r.startContainer as Text, offset: r.startOffset };
  }
  return null;
}

function pointOf(evt: MouseEvent | TouchEvent): { clientX: number; clientY: number } | null {
  if ('touches' in evt) {
    const t = evt.changedTouches?.[0] ?? evt.touches?.[0];
    return t ? { clientX: t.clientX, clientY: t.clientY } : null;
  }
  return { clientX: evt.clientX, clientY: evt.clientY };
}

/**
 * Étend l'offset dans `raw` au "mot" autour — un mot au sens large : lettres,
 * chiffres, souligné, +/-/=/./,/× et Unicode lettres. Un peu large pour
 * capturer les tokens du genre "AB=4" comme UN mot.
 */
export function wordAround(raw: string, offset: number): string | null {
  if (offset < 0 || offset > raw.length) return null;
  const isWord = (ch: string) => /[\p{L}\p{N}_+\-=.,°%×÷/]/u.test(ch);
  let start = offset;
  while (start > 0 && isWord(raw[start - 1]!)) start -= 1;
  let end = offset;
  while (end < raw.length && isWord(raw[end]!)) end += 1;
  const w = raw.slice(start, end).trim();
  return w || null;
}

export interface UseClueOverlayOptions {
  /** Ref du wrapper "scope" — l'élève clique dedans → on capte. */
  scopeRef: React.RefObject<HTMLElement | null>;
  /** Actif seulement pendant la chasse. False = pas de listener. */
  enabled: boolean;
  /** Appelé sur chaque clic dans le scope, avec le texte extrait (ou null). */
  onWordClick: (text: string | null, evt: MouseEvent | TouchEvent) => void;
}

export function useClueOverlay({ scopeRef, enabled, onWordClick }: UseClueOverlayOptions) {
  useEffect(() => {
    if (!enabled) return;
    const el = scopeRef.current;
    if (!el) return;

    function handle(evt: MouseEvent | TouchEvent) {
      // Ignore les clics sur les inputs / boutons pour ne pas gêner les vrais UI.
      const t = evt.target as Element | null;
      if (t?.closest('button,input,select,textarea,a')) return;
      const word = extractClickedText(evt);
      onWordClick(word, evt);
    }

    el.addEventListener('click', handle);
    return () => {
      el.removeEventListener('click', handle);
    };
  }, [enabled, onWordClick, scopeRef]);
}
